import { useEffect, useState, useMemo, useCallback } from "react";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import Card from "../card/Card";
import {
  addByIncrement,
  decreaseCart,
  incrementCart,
  removeFromCart,
} from "../../store/reducers/cartSlice";
import api from "../../api";
import { useProducts } from "../../apiHooks/useProduct";
import imgUrl from "../../imgUrl";
import { useSettings } from "../../context/SettingsContext";
import { useTranslation } from "react-i18next";

/* ─── Flash Sale Countdown ──────────────────────────────── */
function useCountdown(endDate) {
  const calc = useCallback(() => {
    if (!endDate) return null;
    const diff = new Date(endDate) - new Date();
    if (diff <= 0) return { h: 0, m: 0, s: 0, expired: true };
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return { h, m, s, expired: false };
  }, [endDate]);

  const [time, setTime] = useState(calc);
  useEffect(() => {
    if (!endDate) return;
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, [endDate, calc]);
  return time;
}
/* ─── Flash Sale Countdown (kept for reference, timer now site-wide) ──── */
const pad = (n) => String(n).padStart(2, "0");

/* ─── Skeleton ──────────────────────────────────────────── */
const Skeleton = ({ className }) => (
  <div className={`bg-gray-200 animate-pulse rounded ${className}`} />
);

/* ─── Star Rating ───────────────────────────────────────── */
const StarRating = ({ rating = 4.5, total = 0 }) => {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-2">
      <div className="flex">
        {stars.map((s) => (
          <svg
            key={s}
            className={`w-4 h-4 ${s <= Math.floor(rating) ? "text-amber-400" : s - 0.5 <= rating ? "text-amber-300" : "text-gray-200"}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <span className="text-sm text-gray-500">({total} reviews)</span>
    </div>
  );
};

/* ─── Info Badge ────────────────────────────────────────── */
const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
      {icon}
    </div>
    <div>
      <p className="text-xs text-gray-500 font-medium">{label}</p>
      <p className="text-sm text-gray-800 font-semibold mt-0.5">{value}</p>
    </div>
  </div>
);

/* ─── Variant Selector ──────────────────────────────────── */
const VariantSelector = ({ variants, selectedVariant, onSelect }) => {
  // Group attribute values by attribute
  const attributeGroups = useMemo(() => {
    const groups = {};
    variants.forEach((v) => {
      (v.attribute_values || []).forEach((av) => {
        const attrName = av.attribute?.name || "Option";
        if (!groups[attrName]) groups[attrName] = [];
        if (!groups[attrName].find((x) => x.value === av.value)) {
          groups[attrName].push(av);
        }
      });
    });
    return groups;
  }, [variants]);

  const [selectedValues, setSelectedValues] = useState({});

  // Find matching variant when selection changes
  useEffect(() => {
    if (Object.keys(selectedValues).length === 0) return;
    const match = variants.find((v) => {
      const avs = v.attribute_values || [];
      return Object.entries(selectedValues).every(([attrName, val]) =>
        avs.some((av) => av.attribute?.name === attrName && av.value === val),
      );
    });
    onSelect(match || null);
  }, [selectedValues, variants, onSelect]);

  const toggleValue = (attrName, value) => {
    setSelectedValues((prev) =>
      prev[attrName] === value
        ? { ...prev, [attrName]: undefined }
        : { ...prev, [attrName]: value },
    );
  };

  return (
    <div className="space-y-2.5 sm:space-y-4 my-3 sm:my-5">
      {Object.entries(attributeGroups).map(([attrName, values]) => (
        <div key={attrName}>
          <p className="text-sm font-semibold text-gray-700 mb-1 sm:mb-2">
            {attrName}:{" "}
            {selectedValues[attrName] && (
              <span className="font-normal text-emerald-600">
                {selectedValues[attrName]}
              </span>
            )}
          </p>
          <div className="flex flex-wrap gap-2">
            {values.map((av) => {
              const isSelected = selectedValues[attrName] === av.value;
              const isColor = av.color_code;
              return isColor ? (
                <button
                  key={av.id}
                  title={av.value}
                  onClick={() => toggleValue(attrName, av.value)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    isSelected
                      ? "border-gray-800 scale-110 shadow-md"
                      : "border-gray-300 hover:border-gray-500"
                  }`}
                  style={{ backgroundColor: av.color_code }}
                />
              ) : (
                <button
                  key={av.id}
                  onClick={() => toggleValue(attrName, av.value)}
                  className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                    isSelected
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : "border-gray-200 text-gray-700 hover:border-emerald-400 hover:text-emerald-600"
                  }`}
                >
                  {av.value}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {selectedVariant && (
        <p className="text-xs text-gray-400 mt-1">
          SKU: <span className="font-mono">{selectedVariant.sku}</span>
        </p>
      )}
    </div>
  );
};

/* ─── Main Component ────────────────────────────────── */
function Product() {
  const settings = useSettings();
  const { currencySymbol } = settings;
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(1);
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("description");
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const { slug } = useParams(); // Now slug-based
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);

  const cartItem = cart.cartItems.find(
    (item) =>
      item.id === product?.id &&
      (product?.has_variants
        ? item.selectedVariant?.id === selectedVariant?.id
        : !item.selectedVariant),
  );

  /* fetch product by slug */
  useEffect(() => {
    setIsLoading(true);
    setSelectedVariant(null);
    window.scrollTo(0, 0);
    api
      .get(`/products/slug/${slug}`)
      .then((res) => {
        const p = res?.data?.data;
        setProduct(p);
        if (p?.video_url) {
          setSelectedImage("video");
        } else {
          setSelectedImage(p?.main_image);
        }
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [slug]);

  /* update display image when variant changes */
  useEffect(() => {
    if (selectedVariant?.image) {
      setSelectedImage(selectedVariant.image);
    }
  }, [selectedVariant]);

  /* related products — same category */
  const relatedParams = useMemo(
    () => ({
      category_id: product?.category_id ?? undefined,
      per_page: 10,
      paginate: false,
    }),
    [product?.category_id],
  );

  const { data: relatedData } = useProducts(relatedParams, {
    enabled: !!product?.category_id,
  });
  const relatedProducts = (relatedData?.items ?? [])
    .filter((p) => p.id !== product?.id)
    .slice(0, 6);

  // Price: if variant selected use its effective_price, else product price
  const effectivePrice = selectedVariant
    ? Number(selectedVariant.effective_price ?? 0)
    : Number(product?.offer_price ?? product?.price ?? 0);
  const originalPrice = Number(product?.price ?? 0);
  const offerPriceNum = product?.offer_price ? Number(product.offer_price) : 0;
  const hasDiscount =
    !selectedVariant && offerPriceNum > 0 && offerPriceNum < originalPrice;
  const discountPct = hasDiscount
    ? Math.round(((originalPrice - offerPriceNum) / originalPrice) * 100)
    : product?.discount_percentage
      ? Math.ceil(Number(product.discount_percentage))
      : 0;

  const inStock = selectedVariant
    ? selectedVariant.stock_quantity > 0
    : (product?.stock_quantity ?? 0) > 0;

  const handleAddToCart = () => {
    const stock = selectedVariant
      ? (selectedVariant.stock_quantity ?? Infinity)
      : (product?.stock_quantity ?? Infinity);
    const existing = cartItem?.cartQuantity ?? 0;
    const willAdd = Math.min(quantity, stock - existing);

    if (willAdd <= 0) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: "Maximum stock reached!",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    dispatch(
      addByIncrement({
        product: product?.has_variants
          ? { ...product, selectedVariant }
          : product,
        cartQuantity: willAdd,
      }),
    );

    if (existing + willAdd >= stock) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: `Only ${stock} in stock — cart updated to max.`,
        showConfirmButton: false,
        timer: 2500,
      });
    }
  };

  const handleDecrease = () => {
    const itemPayload = product?.has_variants
      ? { ...product, selectedVariant }
      : product;
    if (cartItem?.cartQuantity === 1) {
      dispatch(removeFromCart(itemPayload));
    } else {
      dispatch(decreaseCart(itemPayload));
    }
  };

  const handleOrderNow = () => {
    const stock = selectedVariant
      ? (selectedVariant.stock_quantity ?? Infinity)
      : (product?.stock_quantity ?? Infinity);
    const existing = cartItem?.cartQuantity ?? 0;
    const willAdd = Math.min(quantity, stock - existing);

    if (willAdd > 0) {
      dispatch(
        addByIncrement({
          product: product?.has_variants
            ? { ...product, selectedVariant }
            : product,
          cartQuantity: willAdd,
        }),
      );
    }
    navigate("/checkout");
  };

  // Stock for UI indicators
  const stockQty = selectedVariant
    ? (selectedVariant.stock_quantity ?? Infinity)
    : (product?.stock_quantity ?? Infinity);
  const atMaxStock = (cartItem?.cartQuantity ?? 0) >= stockQty;

  const siteTitle = "Palli Market";
  const productTitle = product?.meta_title || product?.name || siteTitle;
  const productDesc =
    product?.meta_description ||
    (product?.description ? product.description.slice(0, 160) : "");
  const productImage = product?.main_image
    ? `${imgUrl}/${product.main_image}`
    : "";

  const tabs = [
    { id: "description", label: t("product.tabDescription") },
    { id: "shipping", label: t("product.tabShipping") },
  ];

  /** Convert any YouTube URL, including Shorts, to an embed URL */
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;

    const regExp =
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/))([\w-]{11})/;

    const match = url.match(regExp);

    if (!match) return null;

    return `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ─── SEO Helmet ────────────────────────────────────── */}
      <Helmet>
        <title>
          {productTitle} | {siteTitle}
        </title>
        <meta name="description" content={productDesc} />
        <link rel="canonical" href={window.location.href} />
        {/* Open Graph */}
        <meta property="og:title" content={`${productTitle} | ${siteTitle}`} />
        <meta property="og:description" content={productDesc} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content={siteTitle} />
        {productImage && <meta property="og:image" content={productImage} />}
        {product?.price && (
          <meta property="product:price:amount" content={effectivePrice} />
        )}
        {product?.price && (
          <meta property="product:price:currency" content="BDT" />
        )}
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${productTitle} | ${siteTitle}`} />
        <meta name="twitter:description" content={productDesc} />
        {productImage && <meta name="twitter:image" content={productImage} />}
        {/* JSON-LD Product Schema */}
        {product && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Product",
              name: product.name,
              description: product.description || productDesc,
              sku: product.sku || undefined,
              image: productImage ? [productImage] : undefined,
              brand: { "@type": "Brand", name: siteTitle },
              offers: {
                "@type": "Offer",
                url: window.location.href,
                priceCurrency: "BDT",
                price: String(effectivePrice),
                availability: inStock
                  ? "https://schema.org/InStock"
                  : "https://schema.org/OutOfStock",
                seller: { "@type": "Organization", name: siteTitle },
              },
              ...(product.rating
                ? {
                    aggregateRating: {
                      "@type": "AggregateRating",
                      ratingValue: String(product.rating),
                      reviewCount: String(product.review_count || 1),
                    },
                  }
                : {}),
            })}
          </script>
        )}
      </Helmet>

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-10 py-4 sm:py-8 pb-20 lg:pb-8">
        {/* ─── Breadcrumb ─────────────────────────────── */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <button
            onClick={() => navigate("/")}
            className="hover:text-emerald-600 transition"
          >
            {t("product.home")}
          </button>
          <svg
            className="w-4 h-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          <button
            onClick={() => navigate("/shop")}
            className="hover:text-emerald-600 transition"
          >
            {t("product.shop")}
          </button>
          {product?.category && (
            <>
              <svg
                className="w-4 h-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              <button
                onClick={() =>
                  navigate(`/search?category_id=${product.category_id}`)
                }
                className="hover:text-emerald-600 transition"
              >
                {product.category?.name}
              </button>
            </>
          )}
          <svg
            className="w-4 h-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          <span className="text-gray-800 font-medium truncate max-w-[200px]">
            {isLoading ? t("product.loading") : product?.name}
          </span>
        </nav>

        {/* ─── Main Product Card ───────────────────────── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* ── Left: Image ─────────────────────────── */}
            <div className="lg:w-5/12 xl:w-4/12 p-3 sm:p-6 lg:p-10 flex flex-col gap-3 sm:gap-4 border-b lg:border-b-0 lg:border-r border-gray-100">
              {isLoading ? (
                <Skeleton className="w-full aspect-square" />
              ) : (
                <>
                  {/* Discount badge */}
                  {discountPct > 0 && (
                    <div className="self-start bg-orange-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                      {discountPct}% OFF
                    </div>
                  )}
                  {/* Main image / Video container */}
                  <div className="relative rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center aspect-square max-h-[300px] sm:max-h-none mx-auto w-full">
                    {selectedImage === "video" && product?.video_url ? (
                      (() => {
                        const embedUrl = getYouTubeEmbedUrl(product.video_url);
                        return embedUrl ? (
                          <iframe
                            src={embedUrl}
                            title={`${product.name} - Product Video`}
                            className="w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <div className="text-center p-4">
                            <p className="text-gray-400 text-xs">
                              Invalid Video Link
                            </p>
                          </div>
                        );
                      })()
                    ) : (
                      <img
                        src={`${imgUrl}/${selectedImage}`}
                        alt={product?.name}
                        className="w-full h-full object-contain transition-transform duration-300 hover:scale-105"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/400";
                        }}
                      />
                    )}
                    {!inStock && (
                      <div className="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center">
                        <span className="bg-red-100 text-red-600 text-sm font-bold px-4 py-2 rounded-full">
                          {t("product.outOfStock")}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Thumbnail gallery */}
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {product?.video_url && (
                      <button
                        onClick={() => setSelectedImage("video")}
                        className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition flex items-center justify-center bg-slate-900 text-white relative ${
                          selectedImage === "video"
                            ? "border-emerald-500"
                            : "border-gray-200 hover:border-gray-400"
                        }`}
                        title="Play Video"
                      >
                        <svg
                          className="w-8 h-8 text-emerald-500"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        <span className="absolute bottom-0.5 text-[9px] font-bold tracking-wide text-gray-300 uppercase">
                          Video
                        </span>
                      </button>
                    )}
                    {[product?.main_image, ...(product?.gallery_images ?? [])]
                      .filter(Boolean)
                      .map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedImage(img)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition ${
                            selectedImage === img
                              ? "border-emerald-500"
                              : "border-gray-200 hover:border-gray-400"
                          }`}
                        >
                          <img
                            src={`${imgUrl}/${img}`}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                  </div>
                </>
              )}
            </div>

            {/* ── Right: Details ───────────────────────── */}
            <div className="flex-1 p-3 sm:p-6 lg:p-10">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-8 w-3/4" />
                  <Skeleton className="h-5 w-1/3" />
                  <Skeleton className="h-10 w-1/2" />
                  <Skeleton className="h-32 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <>
                  {/* Status badge */}
                  <div className="flex items-center gap-3 mb-2 sm:mb-3">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        inStock
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-red-100 text-red-600"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${inStock ? "bg-emerald-500" : "bg-red-500"}`}
                      />
                      {inStock ? t("product.inStock") : t("product.outOfStock")}
                    </span>
                    {(selectedVariant?.sku || product?.sku) && (
                      <span className="text-xs text-gray-400 font-mono">
                        SKU: {selectedVariant?.sku || product.sku}
                      </span>
                    )}
                  </div>

                  {/* Product name */}
                  <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-1.5 sm:mb-3 leading-tight">
                    {product?.name}
                  </h1>

                  {/* Rating */}
                  {/* <StarRating
                    rating={product?.rating ?? 4.5}
                    total={product?.review_count ?? 0}
                  /> */}

                  {/* Price */}
                  <div className="flex items-baseline gap-2 sm:gap-3 mt-3 sm:mt-5 mb-1.5 sm:mb-2">
                    <span className="text-2xl sm:text-3xl font-extrabold text-gray-900">
                      {currencySymbol} {Number(effectivePrice).toLocaleString()}
                    </span>
                    {hasDiscount && (
                      <del className="text-base sm:text-xl text-gray-400 font-normal">
                        {currencySymbol}{" "}
                        {Number(originalPrice).toLocaleString()}
                      </del>
                    )}
                    {hasDiscount && (
                      <span className="text-xs sm:text-sm font-semibold text-orange-600 bg-orange-50 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full">
                        {t("product.save")} {currencySymbol}{" "}
                        {Number(
                          originalPrice - effectivePrice,
                        ).toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Category */}
                  {product?.category?.name && (
                    <p className="text-xs sm:text-sm text-gray-500 mb-1.5 sm:mb-2">
                      {t("product.category")}:{" "}
                      <button
                        onClick={() =>
                          navigate(`/search?category_id=${product.category_id}`)
                        }
                        className="font-semibold text-emerald-600 hover:text-emerald-700 hover:underline transition"
                      >
                        {product.category.name}
                      </button>
                    </p>
                  )}

                  {/* Short description */}
                  {product?.description && (
                    <p className="text-xs sm:text-sm text-gray-600 leading-normal sm:leading-relaxed mb-1.5 sm:mb-2 line-clamp-3">
                      {product.description}
                    </p>
                  )}

                  {/* ─── Variant Selector ──────────────── */}
                  {product?.has_variants && product?.variants?.length > 0 && (
                    <VariantSelector
                      variants={product.variants}
                      selectedVariant={selectedVariant}
                      onSelect={setSelectedVariant}
                    />
                  )}

                  {/* Divider */}
                  <div className="border-t border-gray-100 mb-4 sm:mb-6 mt-3 sm:mt-4" />

                  {/* Quantity + Add to Cart + Order Now */}
                  {cartItem ? (
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <div className="flex items-center border-2 border-emerald-500 rounded-lg overflow-hidden bg-white">
                        <button
                          onClick={handleDecrease}
                          className="w-9 h-9 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 transition"
                        >
                          {cartItem.cartQuantity === 1 ? (
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          ) : (
                            <span className="text-xl font-bold">−</span>
                          )}
                        </button>
                        <span className="w-10 text-center font-bold text-gray-900">
                          {cartItem.cartQuantity}
                        </span>
                        <button
                          onClick={() =>
                            dispatch(
                              incrementCart(
                                product?.has_variants
                                  ? { ...product, selectedVariant }
                                  : product,
                              ),
                            )
                          }
                          disabled={atMaxStock}
                          title={atMaxStock ? "Maximum stock reached" : ""}
                          className={`w-9 h-9 flex items-center justify-center text-emerald-600 hover:bg-emerald-50 transition text-xl font-bold ${atMaxStock ? "opacity-30 cursor-not-allowed" : ""}`}
                        >
                          +
                        </button>
                      </div>

                      <button
                        onClick={() => navigate("/checkout")}
                        className="h-10 px-3 sm:px-6 flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex-1 sm:flex-initial whitespace-nowrap"
                      >
                        <span className="hidden sm:inline">⚡ </span>অর্ডার করুন
                      </button>

                      <span className="text-xs text-emerald-600 font-semibold bg-emerald-50 border border-emerald-100 px-2.5 py-1.5 rounded-lg whitespace-nowrap">
                        {t("product.addedToCart")}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                      <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden bg-white">
                        <button
                          onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                          disabled={quantity <= 1}
                          className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition font-bold disabled:opacity-40"
                        >
                          −
                        </button>
                        <span className="w-10 text-center font-bold text-gray-900 text-sm">
                          {quantity}
                        </span>
                        <button
                          onClick={() => setQuantity((q) => q + 1)}
                          disabled={!inStock}
                          className="w-9 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition font-bold disabled:opacity-40"
                        >
                          +
                        </button>
                      </div>

                      <button
                        disabled={
                          !inStock ||
                          (product?.has_variants && !selectedVariant)
                        }
                        onClick={handleAddToCart}
                        className="h-10 px-2.5 sm:px-5 flex items-center justify-center gap-1 sm:gap-2 border border-emerald-500 text-emerald-600 hover:bg-emerald-50 bg-white disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm whitespace-nowrap"
                      >
                        <svg
                          className="hidden sm:inline-block w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                          />
                        </svg>
                        {!inStock
                          ? t("product.outOfStock")
                          : product?.has_variants && !selectedVariant
                            ? t("product.selectOptions")
                            : t("product.addToCart")}
                      </button>

                      <button
                        disabled={
                          !inStock ||
                          (product?.has_variants && !selectedVariant)
                        }
                        onClick={handleOrderNow}
                        className="h-10 px-3 sm:px-6 flex items-center justify-center gap-1 sm:gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex-1 sm:flex-initial whitespace-nowrap"
                      >
                        <span className="hidden sm:inline">⚡ </span>
                        {!inStock
                          ? t("product.outOfStock")
                          : product?.has_variants && !selectedVariant
                            ? t("product.selectOptions")
                            : "অর্ডার করুন"}
                      </button>
                    </div>
                  )}

                  {/* Trust badges */}
                  <div className="hidden sm:grid grid-cols-2 gap-2 mt-6">
                    {[
                      {
                        icon: "🚚",
                        label: t("product.trustFreeDelivery", {
                          symbol: currencySymbol,
                        }),
                      },
                      { icon: "↩️", label: t("product.trustReturn") },
                      { icon: "💳", label: t("product.trustSecure") },
                      { icon: "✅", label: t("product.trustGenuine") },
                    ].map((b) => (
                      <div
                        key={b.label}
                        className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-600"
                      >
                        <span>{b.icon}</span>
                        {b.label}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ─── Tab Panel ──────────────────────────────── */}
        {!isLoading && product && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 mt-6 overflow-hidden">
            <div className="flex border-b border-gray-100 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-6 py-4 text-sm font-semibold transition-colors duration-200 border-b-2 ${
                    activeTab === tab.id
                      ? "border-emerald-500 text-emerald-600"
                      : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-4 sm:p-6 lg:p-10">
              {activeTab === "description" && (
                <div className="space-y-5 sm:space-y-8">
                  {/* Description Section */}
                  <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed">
                    {product.description ? (
                      product.description.split("\n").map((para, i) => (
                        <p key={i} className="mb-4">
                          {para}
                        </p>
                      ))
                    ) : (
                      <p className="text-gray-400 italic">
                        {t("product.noDescription")}
                      </p>
                    )}
                  </div>

                  {/* Product Information Section */}
                  <div className="border-t border-gray-100 pt-4 sm:pt-6">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-800 mb-2 sm:mb-4 flex items-center gap-2">
                      <span className="w-1 h-5 bg-emerald-500 rounded-full block"></span>
                      {t("product.tabDetails")}
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 sm:gap-4 mb-4 sm:mb-6">
                      {[
                        { label: t("product.detailName"), value: product.name },
                        {
                          label: t("product.detailSku"),
                          value: product.sku || "N/A",
                        },
                        {
                          label: t("product.detailCategory"),
                          value: product.category?.name || "N/A",
                        },
                        {
                          label: t("product.detailUnit"),
                          value: product.unit || "N/A",
                        },
                        {
                          label: t("product.detailStock"),
                          value: product.stock_quantity ?? "N/A",
                        },
                        {
                          label: t("product.detailStatus"),
                          value: inStock
                            ? t("product.inStock")
                            : t("product.outOfStock"),
                        },
                      ].map(({ label, value }) => (
                        <div
                          key={label}
                          className="flex justify-between items-center py-1.5 sm:py-3 border-b border-gray-50"
                        >
                          <span className="text-sm text-gray-500">{label}</span>
                          <span className="text-sm font-semibold text-gray-800">
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Specifications Section */}
                  {Array.isArray(product.specifications) &&
                    product.specifications.length > 0 && (
                      <div className="border-t border-gray-100 pt-6">
                        <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                          <span className="w-1 h-5 bg-emerald-500 rounded-full block"></span>
                          Specifications
                        </h3>
                        <div className="overflow-hidden rounded-xl border border-gray-100">
                          <table className="w-full text-sm">
                            <tbody>
                              {product.specifications.map((spec, i) => (
                                <tr
                                  key={i}
                                  className={
                                    i % 2 === 0 ? "bg-gray-50" : "bg-white"
                                  }
                                >
                                  <td className="py-2.5 px-4 text-gray-500 font-medium w-1/2 border-r border-gray-100">
                                    {spec.key}
                                  </td>
                                  <td className="py-2.5 px-4 text-gray-800 font-semibold">
                                    {spec.value}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                </div>
              )}

              {activeTab === "shipping" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    {
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
                          />
                        </svg>
                      ),
                      label: t("product.shippingFreeLabel"),
                      value: t("product.shippingFreeValue", {
                        symbol: currencySymbol,
                      }),
                    },
                    {
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 10h10a8 8 0 018 8v2M3 10l4-4m-4 4l4 4"
                          />
                        </svg>
                      ),
                      label: t("product.shippingReturnLabel"),
                      value: t("product.shippingReturnValue"),
                    },
                    {
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      ),
                      label: t("product.shippingCODLabel"),
                      value: t("product.shippingCODValue"),
                    },
                    {
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                      ),
                      label: t("product.shippingSecureLabel"),
                      value: t("product.shippingSecureValue"),
                    },
                  ].map(({ icon, label, value }) => (
                    <InfoItem
                      key={label}
                      icon={icon}
                      label={label}
                      value={value}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Related Products ────────────────────────── */}
        {relatedProducts.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-800">
                {t("product.relatedProducts")}
              </h2>
              <button
                onClick={() =>
                  navigate(`/search?category_id=${product?.category_id}`)
                }
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition"
              >
                {t("product.viewAll")}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
              {relatedProducts.map((item) => (
                <Card key={item.id} data={item} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky Bottom Bar for Mobile Devices */}
      {!isLoading && product && inStock && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2.5 px-3 z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] flex items-center justify-between gap-2">
          {/* Product Thumbnail & Brief Info */}
          <div className="flex items-center gap-2 max-w-[30%] xs:max-w-[35%] sm:max-w-[45%]">
            <img
              src={`${imgUrl}/${selectedVariant?.image || product.main_image}`}
              alt=""
              className="w-10 h-10 object-contain rounded bg-gray-50 border border-gray-100 flex-shrink-0"
              onError={(e) => {
                e.target.src = "https://via.placeholder.com/100";
              }}
            />
            <div className="min-w-0">
              <p className="text-xs font-bold text-gray-800 truncate">
                {product.name}
              </p>
              <p className="text-xs font-extrabold text-emerald-600 mt-0.5">
                {currencySymbol} {Number(effectivePrice).toLocaleString()}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex-1 flex items-center justify-end gap-2">
            {cartItem ? (
              <>
                <div className="flex items-center border border-emerald-500 rounded-lg overflow-hidden h-9 bg-white">
                  <button
                    onClick={handleDecrease}
                    className="w-7 h-full flex items-center justify-center text-emerald-600 hover:bg-emerald-50 transition font-bold"
                  >
                    −
                  </button>
                  <span className="w-8 text-center font-bold text-gray-900 text-xs">
                    {cartItem.cartQuantity}
                  </span>
                  <button
                    onClick={() =>
                      dispatch(
                        incrementCart(
                          product?.has_variants
                            ? { ...product, selectedVariant }
                            : product,
                        ),
                      )
                    }
                    disabled={atMaxStock}
                    className="w-7 h-full flex items-center justify-center text-emerald-600 hover:bg-emerald-50 transition font-bold disabled:opacity-30"
                  >
                    +
                  </button>
                </div>
                <button
                  onClick={() => navigate("/checkout")}
                  className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg shadow transition-all duration-200"
                >
                  অর্ডার করুন
                </button>
              </>
            ) : (
              <>
                <button
                  disabled={product?.has_variants && !selectedVariant}
                  onClick={handleAddToCart}
                  className="h-9 px-3 border border-emerald-500 text-emerald-600 bg-white hover:bg-emerald-50 text-xs font-bold rounded-lg transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {product?.has_variants && !selectedVariant
                    ? t("product.select")
                    : t("product.addToCart")}
                </button>
                <button
                  disabled={product?.has_variants && !selectedVariant}
                  onClick={handleOrderNow}
                  className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold rounded-lg shadow-md transition-all duration-200 flex-1 text-center disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {product?.has_variants && !selectedVariant
                    ? t("product.select")
                    : "অর্ডার করুন"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Product;
